/**
 * Autopilot Onboarding Wizard — Krysia Mode.
 * 8-step flow.
 *
 * Tracking quality checks (Pixel, CAPI, lead-quality scoring, GHL webhook) are
 * not surfaced as a wizard step — they run automatically via preflight before
 * activation. UI surface is reserved for inputs the operator must actually
 * decide on (funnel, KPI, budgets, Meta account, Slack, mode, creatives).
 */
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ChevronRight,
  ChevronLeft,
  ShoppingCart,
  UserPlus,
  Phone,
  Beaker,
  Zap,
  CheckCircle2,
  Loader2,
  Plus,
  FileText,
} from "lucide-react";
import {
  useCreateAutopilotSession,
  useBenchmarkBand,
  useStartAutopilotOnboarding,
  useAutopilotOnboardingProgress,
  type OnboardingStep,
} from "@/hooks/useAutopilot";
import { useAgencyClients } from "@/hooks/useAgencyClients";
import {
  useBrandReadiness,
  useScrapeWebsite,
  useImportFromScrape,
  useCreateFirmAsset,
  useProxyUploadFile,
} from "@/hooks/useFirmAssets";
import { useToast } from "@/hooks/use-toast";
import { FriendlyErrorMapper } from "@/lib/errors/FriendlyErrorMapper";
import { apiRequest } from "@/lib/queryClient";
import { Slack as SlackIcon, ExternalLink } from "lucide-react";

interface OfferSheetSummary {
  id: string;
  title: string;
  content: string;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

interface CreativeConceptSummary {
  id: string;
  label: string;
  hint: string;
  bestFor: ReadonlyArray<string>;
}

const FUNNELS = [
  {
    id: "ecom",
    label: "E-commerce",
    icon: ShoppingCart,
    hint: "Sklep online — Theriot CBO + Advantage+ broad",
  },
  {
    id: "lead_gen",
    label: "Lead Generation",
    icon: UserPlus,
    hint: "Direct response cold — Haynes ABO + 25-30 ads",
  },
  {
    id: "book_call",
    label: "Book a Call",
    icon: Phone,
    hint: "Call funnel + Hammer Them post-booking",
  },
  {
    id: "trial_saas",
    label: "Trial SaaS",
    icon: Beaker,
    hint: "Free trial → paid; conditional CAPI",
  },
  { id: "direct_sale", label: "Direct Sale", icon: Zap, hint: "Impulse purchase, short cycle" },
] as const;

type FunnelId = (typeof FUNNELS)[number]["id"];

// Theriot/Haynes defaults dla MINIMALNEGO wolumenu testów per funnel.
// To jest "podłoga" — tyle reklam autopilot MUSI uruchomić w oknie żeby uczyć się statystycznie.
const VOLUME_DEFAULTS: Record<FunnelId, { weekly: number; monthly: number }> = {
  ecom: { weekly: 3, monthly: 12 },
  lead_gen: { weekly: 6, monthly: 25 },
  book_call: { weekly: 6, monthly: 25 },
  direct_sale: { weekly: 4, monthly: 15 },
  trial_saas: { weekly: 4, monthly: 15 },
};

type CreativeType = "graphics" | "video_ai" | "ugc" | "carousel";
type CreativeMode = "ai_generated" | "manual_upload" | "mix";

interface WizardState {
  funnelType: FunnelId | "";
  industry: string;
  market: "PL" | "US" | "EU" | "UK" | "";
  primaryKpiTarget: number;
  primaryKpiMetric: "NCPA" | "CPA" | "CPL" | "CPBC" | "ROAS";
  /** Dodatkowe KPI — autopilot pilnuje WSZYSTKICH, nie tylko primary (np. ROAS + CPL razem). */
  secondaryKpis: Array<{
    metric: "NCPA" | "CPA" | "CPL" | "CPBC" | "ROAS" | "CTR" | "CPM" | "FREQUENCY";
    target: number;
  }>;
  dailyBudgetMax: number;
  weeklyTestVolume: number;
  monthlyTestVolume: number;
  hardDeck: number;
  monthlyBudgetMax: number;
  mode: "observer" | "conservative" | "balanced";
  metaAdAccountId: string;
  inventoryGateEnabled: boolean;
  slackChannelId: string;
  slackThreshold: number;
  slackDailyDigestEnabled: boolean;
  // ── Krok 9: Co testujemy? ──
  /** Skąd autopilot bierze kreacje — AI generation, manual upload od user-a, czy mix obu. */
  creativeMode: CreativeMode;
  /** Typy kreacji do generowania przez autopilota (Theriot Model B). */
  creativeTypes: CreativeType[];
  /** Ile wariantów wizualnych per brief (1-3). User wybiera najlepszy / odrzuca słabe. */
  variantsPerBrief: 1 | 2 | 3;
  /** Czy każda nowa kreacja wymaga akceptacji user-a przed wgraniem do Meta. */
  approvalGateRequired: boolean;
  /** Czy przy aktywacji autopilot ma od razu wygenerować pierwszą partię kreacji. */
  autoGenerateOnActivate: boolean;
  /** Czy zaimportować istniejące reklamy z Meta jako baseline. */
  importExistingMetaAds: boolean;
  // ── Defaulty uploadu na Meta (po zatwierdzeniu wariantu) ──
  /** Auto-upload na Meta od razu po zatwierdzeniu wariantu (bez modala). */
  autoUploadOnApprove: boolean;
  /** Domyślny tryb wgrania używany przez auto-upload + jako pre-selected w modalu. */
  defaultUploadMode: "duplicate_winner" | "duplicate_specific" | "add_to_existing" | "create_new";
  /** Domyślny status nowo utworzonych reklam — PAUSED (bezpiecznie) lub ACTIVE (od razu live). */
  defaultUploadStatus: "PAUSED" | "ACTIVE";
  // ── Krok 9: Oferta klienta (źródło faktów dla brief generatora) ──
  /** Jak wybieramy oferte: existing sheet z biblioteki, paste tekstu, lub pomijamy (gen fall-back do industry). */
  offerSheetMode: "pick" | "paste" | "skip";
  /** ID istniejącego offer_sheet wybranego z biblioteki. */
  offerSheetId: string | null;
  /** Surowy tekst wklejonej oferty — będzie zapisany jako nowy offer_sheet przed activate. */
  offerSheetDraftTitle: string;
  offerSheetDraftContent: string;
  // ── Krok 10: Styl kreacji (concepts + inspirations) ──
  /** 1-3 ID konceptów z CREATIVE_CONCEPT_LIBRARY — autopilot rotuje przez nie generując briefy. */
  creativeConceptIds: string[];
  /**
   * Inspiracje wizualne wgrane przez usera (już zuploadowane do R2 i utworzone jako
   * firm_creative_assets.style_reference). UI trzyma listę widoczną w wizardzie żeby
   * user mógł je przeglądać + usunąć przed submitem.
   */
  inspirationAssets: Array<{ id: string; thumbnailUrl: string; title: string }>;
  /** Ile % wariantów per brief używa inspiration jako reference image (reszta pure generation). */
  inspirationMixRatio: number;
}

const INITIAL: WizardState = {
  funnelType: "",
  industry: "",
  market: "PL",
  primaryKpiTarget: 50,
  primaryKpiMetric: "CPL",
  secondaryKpis: [],
  dailyBudgetMax: 100,
  weeklyTestVolume: 4,
  monthlyTestVolume: 15,
  hardDeck: 30,
  monthlyBudgetMax: 3000,
  mode: "observer",
  metaAdAccountId: "",
  inventoryGateEnabled: false,
  slackChannelId: "",
  slackThreshold: 1000,
  slackDailyDigestEnabled: false,
  creativeMode: "ai_generated",
  creativeTypes: ["graphics", "video_ai"],
  variantsPerBrief: 2,
  approvalGateRequired: true,
  autoGenerateOnActivate: true,
  importExistingMetaAds: true,
  autoUploadOnApprove: false,
  defaultUploadMode: "duplicate_winner",
  defaultUploadStatus: "PAUSED",
  offerSheetMode: "pick",
  offerSheetId: null,
  offerSheetDraftTitle: "",
  offerSheetDraftContent: "",
  creativeConceptIds: [],
  inspirationAssets: [],
  inspirationMixRatio: 0.5,
};

export default function AutopilotWizard() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState(1);
  const [state, setState] = useState<WizardState>(INITIAL);
  const [onboardingSessionId, setOnboardingSessionId] = useState<string | null>(null);
  const create = useCreateAutopilotSession();
  const startOnboarding = useStartAutopilotOnboarding();
  const { data: onboardingProgress } = useAutopilotOnboardingProgress(onboardingSessionId);
  const { selectedClient, selectedClientName, selectedClientId } = useAgencyClients();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Lista offer sheets użytkownika — wizard pozwala wybrać istniejący lub wkleić nowy.
  const offerSheetsQuery = useQuery<OfferSheetSummary[]>({
    queryKey: ["/api/offer-sheets"],
    queryFn: () => apiRequest("GET", "/api/offer-sheets"),
  });
  const createOfferSheetMut = useMutation<
    OfferSheetSummary,
    Error,
    { title: string; content: string }
  >({
    mutationFn: (payload) => apiRequest("POST", "/api/offer-sheets", payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/offer-sheets"] }),
  });

  // Auto-prefill: gdy klient ma offerSheetId, wybieramy go domyślnie.
  useEffect(() => {
    const clientSheetId = selectedClient?.offerSheetId ?? null;
    if (!clientSheetId) return;
    setState((s) =>
      s.offerSheetId ? s : { ...s, offerSheetId: clientSheetId, offerSheetMode: "pick" }
    );
  }, [selectedClient?.offerSheetId]);

  // Creative concept library — read-only, niezmienny per sesja, fetch raz.
  const conceptLibraryQuery = useQuery<CreativeConceptSummary[]>({
    queryKey: ["/api/autopilot/creative-concepts"],
    queryFn: () => apiRequest("GET", "/api/autopilot/creative-concepts"),
    staleTime: 1000 * 60 * 30, // 30 min — concepts to constants
  });
  // Direct (proxy) upload zamiast presign+PUT — wykonujemy upload przez backend
  // żeby ominąć R2 CORS issue (localhost origin nie zawsze jest w bucket allowlist).
  const proxyUploadMut = useProxyUploadFile();
  const createFirmAssetMut = useCreateFirmAsset();

  // When the orchestrator marks onboarding as done, hop to the dashboard.
  // Failure stays on the loader so the operator can read the error.
  useEffect(() => {
    if (!onboardingSessionId || onboardingProgress?.step !== "done") return;
    setLocation(`/admin/autopilot/${onboardingSessionId}`);
  }, [onboardingProgress?.step, onboardingSessionId, setLocation]);
  // Brand readiness dla wybranego klienta — sprawdzane gdy creativeMode wymaga firm-assets.
  const { data: readiness, refetch: refetchReadiness } = useBrandReadiness(
    selectedClientId ?? null
  );
  const scrapeMut = useScrapeWebsite();
  const importMut = useImportFromScrape();
  const [scrapeUrl, setScrapeUrl] = useState("");

  // Wybierz konto Meta z agency_clients.adAccounts (jeśli klient ma podłączone).
  // Pierwszy wpis z platform === 'meta'. Format ID może być z lub bez prefixu 'act_' —
  // walidator wizarda wymaga 'act_X', więc dorabiamy prefix gdy brakuje.
  const clientMetaAccount = selectedClient?.adAccounts?.find((a) => a.platform === "meta") ?? null;
  const clientMetaAccountId = clientMetaAccount
    ? clientMetaAccount.id.startsWith("act_")
      ? clientMetaAccount.id
      : `act_${clientMetaAccount.id}`
    : null;

  // Auto-fill stanu wizarda po załadowaniu klienta (lub jego zmianie w sidebarze).
  // Nadpisuje TYLKO gdy user nic nie wpisał ręcznie — żeby nie nadpisać
  // świadomej edycji.
  useEffect(() => {
    if (!clientMetaAccountId) return;
    setState((s) => (s.metaAdAccountId ? s : { ...s, metaAdAccountId: clientMetaAccountId }));
  }, [clientMetaAccountId]);
  const benchmarkBand = useBenchmarkBand(
    state.funnelType && state.industry && state.market
      ? {
          funnelType: state.funnelType,
          industry: state.industry,
          market: state.market,
          metric: state.primaryKpiMetric === "NCPA" ? "NCPA" : state.primaryKpiMetric,
        }
      : null
  );

  const totalSteps = 10;
  const next = () => setStep((s) => Math.min(totalSteps, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!state.funnelType) return;
    const config: Record<string, unknown> = {};
    if (state.slackChannelId) config.slackChannelId = state.slackChannelId;
    if (state.slackThreshold) config.slackThreshold = state.slackThreshold;
    if (state.slackDailyDigestEnabled) config.slackDailyDigestEnabled = true;
    config.creativeMode = state.creativeMode;
    config.creativeTypes = state.creativeTypes;
    config.variantsPerBrief = state.variantsPerBrief;
    config.approvalGateRequired = state.approvalGateRequired;
    config.autoGenerateOnActivate = state.autoGenerateOnActivate;
    config.importExistingMetaAds = state.importExistingMetaAds;
    config.autoUploadOnApprove = state.autoUploadOnApprove;
    config.defaultUploadMode = state.defaultUploadMode;
    config.defaultUploadStatus = state.defaultUploadStatus;

    // Offer sheet — paste mode tworzy nowy sheet PRZED utworzeniem sesji,
    // pick mode bierze istniejące ID. Skip = brak ofertowego kontekstu w config (legacy).
    let resolvedOfferSheetId: string | null = null;
    if (state.offerSheetMode === "pick" && state.offerSheetId) {
      resolvedOfferSheetId = state.offerSheetId;
    } else if (
      state.offerSheetMode === "paste" &&
      state.offerSheetDraftContent.trim().length >= 50
    ) {
      try {
        const created = await createOfferSheetMut.mutateAsync({
          title: state.offerSheetDraftTitle.trim() || "Oferta autopilota",
          content: state.offerSheetDraftContent.trim(),
        });
        resolvedOfferSheetId = created.id;
      } catch (err) {
        toast({
          title: "Nie udało się zapisać oferty",
          description: FriendlyErrorMapper.map(err).description,
          variant: "destructive",
        });
        return;
      }
    }
    if (resolvedOfferSheetId) {
      config.offerSheetId = resolvedOfferSheetId;
    }
    if (state.creativeConceptIds.length > 0) {
      config.creativeConceptIds = state.creativeConceptIds.slice(0, 3);
    }
    if (state.inspirationAssets.length > 0) {
      // Pełna lista IDs — AssetGenerator załaduje style_reference firm-assets klienta wprost z DB,
      // tu zapisujemy mix ratio + (opcjonalnie) explicit whitelist gdyby user chciał potem ograniczyć.
      config.inspirationAssetIds = state.inspirationAssets.map((a) => a.id);
      config.inspirationMixRatio = state.inspirationMixRatio;
    }

    try {
      const session = await create.mutateAsync({
        funnelType: state.funnelType,
        agencyClientId: selectedClientId ?? undefined,
        industry: state.industry,
        market: state.market,
        primaryKpi: {
          metric: state.primaryKpiMetric,
          target: state.primaryKpiTarget,
          currency: "PLN",
        },
        secondaryKpis: state.secondaryKpis.length > 0 ? state.secondaryKpis : undefined,
        dailyBudgetMax: state.dailyBudgetMax,
        hardDeck: state.hardDeck,
        monthlyBudgetMax: state.monthlyBudgetMax,
        weeklyTestVolume: state.weeklyTestVolume,
        monthlyTestVolume: state.monthlyTestVolume,
        mode: state.mode,
        metaAdAccountId: state.metaAdAccountId || undefined,
        inventoryGateEnabled: state.inventoryGateEnabled,
        config: Object.keys(config).length > 0 ? config : undefined,
      });
      if (!session?.id) {
        toast({
          title: "Sesja stworzona, ale nie udało się odczytać ID",
          description: "Idź ręcznie do listy sesji w Autopilot Ads.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Sesja utworzona — startuję onboarding",
        description: "Pobieram dane z Meta i generuję strategię…",
      });
      try {
        await startOnboarding.mutateAsync(session.id);
      } catch (err) {
        toast({
          title: "Onboarding się nie odpalił",
          description: FriendlyErrorMapper.map(err).description,
          variant: "destructive",
        });
      }
      setOnboardingSessionId(session.id);
    } catch (err) {
      toast({
        title: "Nie udało się utworzyć sesji",
        description: FriendlyErrorMapper.map(err).description,
        variant: "destructive",
      });
    }
  };

  if (onboardingSessionId) {
    return (
      <OnboardingLoader
        sessionId={onboardingSessionId}
        progress={onboardingProgress ?? null}
        onSkip={() => setLocation(`/admin/autopilot/${onboardingSessionId}`)}
      />
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto p-6 max-w-3xl">
        <div className="mb-2">
          <a
            href="/admin/autopilot"
            className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1"
          >
            ← Autopilot Ads
          </a>
        </div>
        <div className="mb-6 flex items-center gap-2">
          <Badge variant="outline">
            Krok {step} / {totalSteps}
          </Badge>
          <h1 className="text-2xl font-bold">Onboarding sesji autopilota</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{stepTitle(step)}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {FUNNELS.map((f) => {
                  const Icon = f.icon;
                  return (
                    <Card
                      key={f.id}
                      className={`cursor-pointer hover:border-primary transition ${state.funnelType === f.id ? "border-primary border-2" : ""}`}
                      onClick={() =>
                        setState((s) => ({
                          ...s,
                          funnelType: f.id,
                          weeklyTestVolume: VOLUME_DEFAULTS[f.id].weekly,
                          monthlyTestVolume: VOLUME_DEFAULTS[f.id].monthly,
                        }))
                      }
                    >
                      <CardContent className="p-4">
                        <Icon className="w-6 h-6 mb-2 text-primary" />
                        <div className="font-semibold">{f.label}</div>
                        <div className="text-xs text-muted-foreground mt-1">{f.hint}</div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {step === 2 && (
              <>
                <Field>
                  <FieldLabel>Branża</FieldLabel>
                  <Input
                    value={state.industry}
                    onChange={(e) => setState((s) => ({ ...s, industry: e.target.value }))}
                    placeholder="np. apparel, supplements, real_estate, b2b"
                  />
                </Field>
                <Field>
                  <FieldLabel>Rynek</FieldLabel>
                  <Select
                    value={state.market}
                    onValueChange={(v) =>
                      setState((s) => ({ ...s, market: v as WizardState["market"] }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PL">PL — Polska</SelectItem>
                      <SelectItem value="US">US — United States</SelectItem>
                      <SelectItem value="EU">EU — strefa euro</SelectItem>
                      <SelectItem value="UK">UK — United Kingdom</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </>
            )}

            {step === 3 && (
              <>
                <Field>
                  <FieldLabel>Główna metryka KPI (najważniejsza)</FieldLabel>
                  <Select
                    value={state.primaryKpiMetric}
                    onValueChange={(v) =>
                      setState((s) => ({
                        ...s,
                        primaryKpiMetric: v as WizardState["primaryKpiMetric"],
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NCPA">NCPA — New Customer CPA (Theriot ecom)</SelectItem>
                      <SelectItem value="CPL">CPL — Cost per Lead (Haynes lead gen)</SelectItem>
                      <SelectItem value="CPBC">CPBC — Cost per Booked Call</SelectItem>
                      <SelectItem value="ROAS">ROAS — Return on Ad Spend</SelectItem>
                      <SelectItem value="CPA">CPA — Cost per Acquisition</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Główny cel autopilota. Podejmuje decyzje optymalizacyjne (scale/kill) bazując na
                    tej metryce.
                  </p>
                </Field>
                <Field>
                  <FieldLabel>Target głównej metryki</FieldLabel>
                  <Input
                    type="number"
                    value={state.primaryKpiTarget}
                    onChange={(e) =>
                      setState((s) => ({ ...s, primaryKpiTarget: Number(e.target.value) || 0 }))
                    }
                  />
                </Field>

                <Field>
                  <FieldLabel>Dodatkowe KPI (autopilot pilnuje WSZYSTKICH)</FieldLabel>
                  <p className="text-xs text-muted-foreground mb-2">
                    Operowanie tylko na ROAS to ślepota. Dodaj 1-3 dodatkowe metryki (CPL, CPM, CTR,
                    frequency), które autopilot będzie monitorować równolegle. Przy scaling sprawdza
                    CAŁOŚĆ — jeśli CPL siada przy wzroście ROAS, decyzja jest zatrzymana.
                  </p>
                  <div className="space-y-2">
                    {state.secondaryKpis.map((kpi, idx) => (
                      <div
                        key={idx}
                        className="flex gap-2 items-end border border-white/10 rounded p-2"
                      >
                        <div className="flex-1">
                          <label className="text-[10px] text-muted-foreground uppercase">
                            Metryka
                          </label>
                          <Select
                            value={kpi.metric}
                            onValueChange={(v) =>
                              setState((s) => ({
                                ...s,
                                secondaryKpis: s.secondaryKpis.map((k, i) =>
                                  i === idx ? { ...k, metric: v as typeof kpi.metric } : k
                                ),
                              }))
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ROAS">ROAS</SelectItem>
                              <SelectItem value="CPL">CPL — Cost per Lead</SelectItem>
                              <SelectItem value="CPA">CPA — Cost per Acquisition</SelectItem>
                              <SelectItem value="NCPA">NCPA — New Customer CPA</SelectItem>
                              <SelectItem value="CPBC">CPBC — Cost per Booked Call</SelectItem>
                              <SelectItem value="CTR">CTR — Click-through rate (%)</SelectItem>
                              <SelectItem value="CPM">CPM — Cost per 1000 impressions</SelectItem>
                              <SelectItem value="FREQUENCY">
                                Frequency (max impressions/user)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex-1">
                          <label className="text-[10px] text-muted-foreground uppercase">
                            Target
                          </label>
                          <Input
                            type="number"
                            step="0.01"
                            value={kpi.target}
                            onChange={(e) =>
                              setState((s) => ({
                                ...s,
                                secondaryKpis: s.secondaryKpis.map((k, i) =>
                                  i === idx ? { ...k, target: Number(e.target.value) || 0 } : k
                                ),
                              }))
                            }
                            className="h-8 text-xs"
                          />
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setState((s) => ({
                              ...s,
                              secondaryKpis: s.secondaryKpis.filter((_, i) => i !== idx),
                            }))
                          }
                          className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                    {state.secondaryKpis.length < 4 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setState((s) => ({
                            ...s,
                            secondaryKpis: [...s.secondaryKpis, { metric: "CTR", target: 1.5 }],
                          }))
                        }
                        className="w-full gap-2 border-dashed"
                      >
                        <Plus className="w-3.5 h-3.5" /> Dodaj kolejne KPI
                      </Button>
                    )}
                  </div>
                </Field>

                {benchmarkBand.data?.band && (
                  <Card className="bg-muted/30">
                    <CardContent className="p-3 text-xs">
                      <div className="font-semibold mb-1">
                        Branżowy band ({benchmarkBand.data.band.source}):
                      </div>
                      <div>
                        p25={benchmarkBand.data.band.p25} · p50={benchmarkBand.data.band.p50} · p75=
                        {benchmarkBand.data.band.p75}
                      </div>
                      {benchmarkBand.data.band.notes && (
                        <div className="mt-1 text-muted-foreground">
                          {benchmarkBand.data.band.notes}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {step === 4 && (
              <>
                <Field>
                  <FieldLabel>Maksymalny dzienny budżet (PLN)</FieldLabel>
                  <Input
                    type="number"
                    value={state.dailyBudgetMax}
                    onChange={(e) =>
                      setState((s) => ({ ...s, dailyBudgetMax: Number(e.target.value) || 0 }))
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Górny limit — autopilot nigdy nie wyda więcej niż tyle dziennie na reklamy tego
                    klienta. Wpisz kwotę w PLN, np. <strong>100</strong> = 100 zł / dzień.
                  </p>
                </Field>
                <Field>
                  <FieldLabel>Minimalny budżet dzienny ("podłoga")</FieldLabel>
                  <Input
                    type="number"
                    value={state.hardDeck}
                    onChange={(e) =>
                      setState((s) => ({ ...s, hardDeck: Number(e.target.value) || 0 }))
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Dolny limit — autopilot nie zejdzie poniżej tej kwoty nawet gdy reklama słabo
                    idzie. Po co: za niski budżet = za mało danych, algorytm Mety nie nauczy się
                    komu pokazywać. Sugerowane: ~30% maksymalnego dziennego.
                  </p>
                </Field>
                <Field>
                  <FieldLabel>Maksymalny miesięczny budżet (PLN)</FieldLabel>
                  <Input
                    type="number"
                    value={state.monthlyBudgetMax}
                    onChange={(e) =>
                      setState((s) => ({ ...s, monthlyBudgetMax: Number(e.target.value) || 0 }))
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Twardy cap miesięczny — autopilot zatrzyma wydatki gdy łączny spend dotknie tej
                    kwoty. Bezpiecznik na wypadek gdyby coś poszło nie tak (np. 30 dni × dzienny
                    max).
                  </p>
                </Field>
                <Field>
                  <FieldLabel>Minimum reklam tygodniowo (do testów)</FieldLabel>
                  <Input
                    type="number"
                    value={state.weeklyTestVolume}
                    onChange={(e) =>
                      setState((s) => ({
                        ...s,
                        weeklyTestVolume: Math.max(1, Number(e.target.value) || 1),
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Ile NOWYCH reklam autopilot ma uruchomić w tygodniu, żeby było z czego wybierać
                    zwycięzców. Mniej = wolniejsze uczenie. Sugerowane: e-commerce{" "}
                    <strong>3</strong>, lead-gen <strong>6</strong>.
                  </p>
                </Field>
                <Field>
                  <FieldLabel>Minimum reklam miesięcznie (do testów)</FieldLabel>
                  <Input
                    type="number"
                    value={state.monthlyTestVolume}
                    onChange={(e) =>
                      setState((s) => ({
                        ...s,
                        monthlyTestVolume: Math.max(1, Number(e.target.value) || 1),
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Łączna minimalna pula testów na miesiąc (≥ 4× tygodniowa). Sugerowane:
                    e-commerce <strong>12</strong>, lead-gen <strong>25</strong>.
                  </p>
                </Field>
                {state.funnelType === "ecom" && (
                  <Field>
                    <FieldLabel>Blokada przy niskim stanie magazynowym</FieldLabel>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={state.inventoryGateEnabled}
                        onChange={(e) =>
                          setState((s) => ({ ...s, inventoryGateEnabled: e.target.checked }))
                        }
                      />
                      <span className="text-xs text-muted-foreground">
                        Wstrzymaj zwiększanie budżetu (+20%) gdy w Shopify zostało mniej niż 14 dni
                        stocku. Chroni przed przepalaniem reklam na produktach które się wyprzedają.
                      </span>
                    </div>
                  </Field>
                )}
              </>
            )}

            {step === 5 && (
              <Field>
                <FieldLabel>Konto reklamowe Meta</FieldLabel>
                {clientMetaAccountId && state.metaAdAccountId === clientMetaAccountId && (
                  <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded px-2 py-1.5 mb-2">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                    <span>
                      Auto-wybrane z klienta <strong>{selectedClientName}</strong>
                      {clientMetaAccount?.name ? <> — {clientMetaAccount.name}</> : null}
                    </span>
                  </div>
                )}
                <Input
                  value={state.metaAdAccountId}
                  onChange={(e) => setState((s) => ({ ...s, metaAdAccountId: e.target.value }))}
                  placeholder="act_1234567890"
                />
                <span className="text-xs text-muted-foreground">
                  {clientMetaAccountId
                    ? "Możesz nadpisać ręcznie jeśli chcesz puścić autopilota na innym koncie."
                    : "Format: act_X gdzie X to ID konta. Wybrany klient nie ma jeszcze podpiętego konta Meta — wpisz ręcznie."}
                </span>
              </Field>
            )}

            {step === 6 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-3">
                  <SlackIcon className="w-5 h-5" />
                  <strong>Slack notifications (opcjonalne)</strong>
                </div>
                <p className="text-xs text-muted-foreground">
                  Pro Mode dostaje powiadomienia Slack o significant decisions (scale ≥ threshold,
                  kill, audience exhaustion). Pomijasz → tylko UI dashboard inbox.
                </p>
                <Field>
                  <FieldLabel>Slack channel ID (np. C12345)</FieldLabel>
                  <Input
                    value={state.slackChannelId}
                    onChange={(e) => setState((s) => ({ ...s, slackChannelId: e.target.value }))}
                    placeholder="C0123ABCDEF"
                  />
                  <a
                    href="/api/slack/oauth/install"
                    target="_blank"
                    rel="noopener"
                    className="text-xs text-primary inline-flex items-center gap-1 mt-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Połącz workspace Slack (OAuth) — otwiera nową kartę
                  </a>
                </Field>
                <Field>
                  <FieldLabel>Próg dla notyfikacji o scaling (PLN)</FieldLabel>
                  <Input
                    type="number"
                    value={state.slackThreshold}
                    onChange={(e) =>
                      setState((s) => ({ ...s, slackThreshold: Number(e.target.value) || 0 }))
                    }
                  />
                  <span className="text-xs text-muted-foreground">
                    Default 1000 PLN — niższy próg → więcej Slack noise.
                  </span>
                </Field>
                <Field>
                  <FieldLabel>Weekly digest (sobota 9am)</FieldLabel>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={state.slackDailyDigestEnabled}
                      onChange={(e) =>
                        setState((s) => ({ ...s, slackDailyDigestEnabled: e.target.checked }))
                      }
                    />
                    <span className="text-xs">
                      Włącz cotygodniowy digest decisions+alerts+snapshots
                    </span>
                  </div>
                </Field>
              </div>
            )}

            {step === 7 && (
              <Field>
                <FieldLabel>Tryb autopilota</FieldLabel>
                <Select
                  value={state.mode}
                  onValueChange={(v) => setState((s) => ({ ...s, mode: v as WizardState["mode"] }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="observer">
                      Observer (default 14d) — tylko loguje, niczego nie zmienia
                    </SelectItem>
                    <SelectItem value="conservative">
                      Conservative — auto-apply scale_+20/wait/hold/rotate; reszta require_human
                    </SelectItem>
                    <SelectItem value="balanced">
                      Balanced — auto-apply wszystko poza kill/relaunch/exhausted
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  Po starcie zalecany tryb observer 14d (shadow mode). Potem przełącz na
                  conservative gdy zaufasz silnikowi.
                </p>
              </Field>
            )}

            {step === 8 && (
              <div className="space-y-5">
                <Field>
                  <FieldLabel>Skąd autopilot bierze kreacje?</FieldLabel>
                  <p className="text-xs text-muted-foreground mb-2">
                    Wybierz kto produkuje grafiki i video. <strong>AI</strong> = autopilot generuje
                    sam (wymaga firm-assets — logo, twarz, kolory). <strong>Ja wgrywam</strong> = ty
                    dostarczasz pliki, autopilot tylko publikuje i scaluje. <strong>Mix</strong> =
                    oba (najwięcej testów).
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {[
                      {
                        id: "ai_generated" as const,
                        label: "🤖 AI generuje",
                        hint: "Autopilot odpala fal/Kling/HeyGen z briefów Claude'a. Wymaga firm-assets dla dobrej jakości.",
                      },
                      {
                        id: "manual_upload" as const,
                        label: "👤 Ja wgrywam",
                        hint: "Ty produkujesz kreacje (ad-creator albo zewn.). Autopilot tylko wgrywa do Meta i zarządza budżetem.",
                      },
                      {
                        id: "mix" as const,
                        label: "🤝 Mix",
                        hint: "AI generuje + ty wgrywasz własne. Największa różnorodność testów.",
                      },
                    ].map((opt) => {
                      const selected = state.creativeMode === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setState((s) => ({ ...s, creativeMode: opt.id }))}
                          className={`border rounded p-3 text-left transition ${selected ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}
                        >
                          <div className="font-medium text-sm">{opt.label}</div>
                          <div className="text-xs text-muted-foreground mt-1">{opt.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                </Field>

                {(state.creativeMode === "ai_generated" || state.creativeMode === "mix") && (
                  <Field>
                    <FieldLabel>Brand readiness — co AI potrzebuje do dobrej generacji</FieldLabel>
                    <p className="text-xs text-muted-foreground mb-2">
                      Bez logo + twarzy/foundera AI generuje "z powietrza" — outputy są generyczne i
                      słabo konwertują.
                      {selectedClientName ? (
                        <>
                          {" "}
                          Sprawdzamy bibliotekę klienta <strong>{selectedClientName}</strong>:
                        </>
                      ) : (
                        <>
                          {" "}
                          <strong>Wybierz najpierw klienta w sidebarze.</strong>
                        </>
                      )}
                    </p>
                    {readiness && (
                      <div className="space-y-1 mb-3">
                        <ReadinessRow
                          ok={readiness.present.logo}
                          label="Logo marki"
                          missing="brand_logo (required)"
                        />
                        <ReadinessRow
                          ok={readiness.present.face}
                          label="Twarz / avatar / founder"
                          missing="avatar_face / founder_video / testimonial (required)"
                        />
                        <ReadinessRow
                          ok={readiness.present.palette}
                          label="Paleta kolorów + czcionki"
                          missing="brand_palette (recommended)"
                        />
                      </div>
                    )}
                    {readiness && !readiness.ready && (
                      <div className="border border-amber-500/40 bg-amber-500/5 rounded p-3 space-y-2">
                        <div className="text-xs font-medium text-amber-200">
                          ⚡ Szybkie uzupełnienie: wklej URL strony klienta
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          Autopilot pobierze logo, paletę kolorów i hero image automatycznie.
                          Brakujące rzeczy (np. founder photo) dodasz potem ręcznie w bibliotece.
                        </p>
                        <div className="flex gap-2">
                          <Input
                            type="url"
                            value={scrapeUrl}
                            onChange={(e) => setScrapeUrl(e.target.value)}
                            placeholder="https://stylowa-xl.pl"
                            className="flex-1"
                          />
                          <Button
                            size="sm"
                            disabled={
                              !scrapeUrl.trim() || scrapeMut.isPending || importMut.isPending
                            }
                            onClick={async () => {
                              try {
                                const result = await scrapeMut.mutateAsync({
                                  url: scrapeUrl.trim(),
                                  agencyClientId: selectedClientId ?? null,
                                });
                                // Auto-import: pierwsze logo (jeśli jest) + wszystkie kolory + hero
                                const logos = result.logoCandidates
                                  .slice(0, 1)
                                  .map((l) => ({ url: l.url, source: l.source }));
                                const imp = await importMut.mutateAsync({
                                  agencyClientId: selectedClientId ?? null,
                                  sourceUrl: result.url,
                                  logos,
                                  colors: result.colors,
                                  fonts: result.fonts,
                                  heroImage: result.heroImage,
                                  siteName: result.siteName,
                                });
                                toast({
                                  title: "Zaimportowano ze strony",
                                  description: `${imp.created} assetów dodanych do biblioteki klienta.`,
                                });
                                setScrapeUrl("");
                                refetchReadiness();
                              } catch (err) {
                                toast({
                                  title: "Nie udało się pobrać",
                                  description: FriendlyErrorMapper.map(err).description,
                                  variant: "destructive",
                                });
                              }
                            }}
                            className="gap-1"
                          >
                            {scrapeMut.isPending || importMut.isPending ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : null}
                            Pobierz
                          </Button>
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                          <a
                            href={selectedClientId ? `/firm-assets` : "#"}
                            target="_blank"
                            rel="noopener"
                            className="text-[11px] text-primary inline-flex items-center gap-1 hover:underline"
                          >
                            Albo otwórz pełną bibliotekę w nowej karcie{" "}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => refetchReadiness()}
                            className="text-[11px] h-6 ml-auto"
                          >
                            Sprawdź ponownie
                          </Button>
                        </div>
                      </div>
                    )}
                    {readiness?.ready && (
                      <div className="border border-emerald-500/40 bg-emerald-500/5 rounded p-2 text-xs text-emerald-300 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 shrink-0" />
                        Wszystko gotowe — AI ma kompletny conditioning bundle (logo + twarz +
                        paleta).
                      </div>
                    )}
                  </Field>
                )}

                {state.creativeMode === "manual_upload" && (
                  <div className="border border-blue-500/40 bg-blue-500/5 rounded p-3 text-xs text-blue-200">
                    <div className="font-medium mb-1">👤 Tryb manual upload</div>
                    <p>
                      Autopilot nie będzie generować kreacji AI. Po aktywacji sesji idziesz do{" "}
                      <strong>Pipeline kreatywny</strong> — wgrywasz swoje grafiki/video do kolumny
                      "Gotowe do testu", potem klikasz "Wgraj na Meta". Reszta autopilota (snapshoty
                      KPI, decyzje scale/kill, Slack notyfikacje) działa normalnie.
                    </p>
                  </div>
                )}

                {(state.creativeMode === "ai_generated" || state.creativeMode === "mix") && (
                  <>
                    <Field>
                      <FieldLabel>Typy kreacji do generowania</FieldLabel>
                      <p className="text-xs text-muted-foreground mb-2">
                        Autopilot będzie generował <strong>tylko zaznaczone</strong> typy. Każda
                        generacja AI = koszt tokenów (płacisz Ty). Zalecane: zaznacz przynajmniej 2
                        dla zdrowego mixu.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {(
                          [
                            {
                              id: "graphics",
                              label: "Statyczne grafiki",
                              hint: "nano-banana-pro (najwyższa jakość AI, idealne dla cold audience).",
                            },
                            {
                              id: "video_ai",
                              label: "Video AI z avatar swap",
                              hint: "HeyGen — twój avatar mówi scriptem. Średni koszt. Najlepszy CTR.",
                            },
                            {
                              id: "ugc",
                              label: "UGC video (talking head)",
                              hint: "AI clone głosu + b-roll. Wymaga voice_sample w firm-assets. Wyższy koszt.",
                            },
                            {
                              id: "carousel",
                              label: "Carousel (5-10 slajdów)",
                              hint: "Multi-frame static. Dobre dla education / proof.",
                            },
                          ] as const
                        ).map((opt) => {
                          const checked = state.creativeTypes.includes(opt.id);
                          return (
                            <label
                              key={opt.id}
                              className={`border rounded p-3 cursor-pointer transition ${checked ? "border-primary bg-primary/5" : "border-white/10 hover:border-white/30"}`}
                            >
                              <div className="flex items-start gap-2">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) =>
                                    setState((s) => ({
                                      ...s,
                                      creativeTypes: e.target.checked
                                        ? Array.from(new Set([...s.creativeTypes, opt.id]))
                                        : s.creativeTypes.filter((t) => t !== opt.id),
                                    }))
                                  }
                                  className="mt-0.5"
                                />
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{opt.label}</div>
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    {opt.hint}
                                  </div>
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </Field>

                    <Field>
                      <FieldLabel>Wariantów per brief (1-3)</FieldLabel>
                      <p className="text-xs text-muted-foreground mb-2">
                        Każdy brief AI dostaje N wariantów wizualnych — wybierasz najlepszy /
                        edytujesz / odrzucasz słabe. Więcej = większe szanse trafić, ale wyższy
                        koszt tokenów.
                      </p>
                      <div className="flex gap-2">
                        {([1, 2, 3] as const).map((n) => {
                          const selected = state.variantsPerBrief === n;
                          return (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setState((s) => ({ ...s, variantsPerBrief: n }))}
                              className={`flex-1 border rounded p-3 transition ${selected ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}
                            >
                              <div className="text-2xl font-bold">{n}</div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {n === 1 && "Najtańsze"}
                                {n === 2 && "Sweet spot"}
                                {n === 3 && "Maks wybór"}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                  </>
                )}

                <Field>
                  <FieldLabel>Approval gate — kto wciska "publikuj na Meta"?</FieldLabel>
                  <p className="text-xs text-muted-foreground mb-2">
                    Niezależnie od wyboru: AI generuje briefy + obrazy/video automatycznie i wrzuca
                    do pipeline. Decydujesz tylko, kto wciska "wgraj na Meta".
                  </p>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={state.approvalGateRequired}
                        onChange={() => setState((s) => ({ ...s, approvalGateRequired: true }))}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium">
                          Wymagam akceptacji każdej kreacji przed Meta
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Gotowe kreacje czekają w pipeline ("Gotowe do testu"). Klikasz "Zaakceptuj
                          i wgraj" → idzie na Meta. Bezpieczniejsze.
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!state.approvalGateRequired}
                        onChange={() => setState((s) => ({ ...s, approvalGateRequired: false }))}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium">
                          Auto-publikuj (autopilot decyduje sam)
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Wygenerowane kreacje idą prosto do Meta zgodnie z volume schedule. Szybsze
                          tempo, ale jeśli AI wygeneruje coś dziwnego — pójdzie live bez Twojej
                          kontroli. Tylko jeśli ufasz.
                        </div>
                      </div>
                    </label>
                  </div>
                </Field>

                <Field>
                  <FieldLabel>Co zrobić zaraz po aktywacji?</FieldLabel>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={state.autoGenerateOnActivate}
                        onChange={(e) =>
                          setState((s) => ({ ...s, autoGenerateOnActivate: e.target.checked }))
                        }
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium">
                          Wygeneruj pierwszą partię kreacji od razu
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Zaraz po starcie autopilot odpali Claude'a (briefy: hook, headline, copy)
                          + generator wizualny (fal/HeyGen) i przygotuje N kompletnych kreacji
                          (zgodnie z weekly volume) do pipeline-a. Bez tego musisz czekać na cron
                          (~24h).
                        </div>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={state.importExistingMetaAds}
                        onChange={(e) =>
                          setState((s) => ({ ...s, importExistingMetaAds: e.target.checked }))
                        }
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium">
                          Zaimportuj moje istniejące reklamy z Meta
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Top-N adsów z konta reklamowego trafi do pipeline-a jako baseline ("co już
                          działa"). AI uczy się stylu Twoich winners przy generacji nowych.
                        </div>
                      </div>
                    </label>
                  </div>
                </Field>

                <Field>
                  <FieldLabel>Auto-upload na Meta po zatwierdzeniu</FieldLabel>
                  <label className="flex items-start gap-2 cursor-pointer mt-1">
                    <input
                      type="checkbox"
                      checked={state.autoUploadOnApprove}
                      onChange={(e) =>
                        setState((s) => ({ ...s, autoUploadOnApprove: e.target.checked }))
                      }
                      className="mt-1"
                    />
                    <div>
                      <div className="text-sm font-medium">
                        Po kliknięciu "Zatwierdź" wariant automatycznie idzie na Meta (bez modala)
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Wymaga że w polach poniżej zdefiniujesz domyślny tryb i status. Bez
                        auto-upload — przy każdym zatwierdzeniu pojawia się modal "Wgraj na Meta"
                        gdzie możesz wybrać ad hoc.
                      </div>
                    </div>
                  </label>
                </Field>

                <Field>
                  <FieldLabel>
                    Domyślny tryb uploadu (jeśli auto-upload włączony, lub jako pre-select w modalu)
                  </FieldLabel>
                  <Select
                    value={state.defaultUploadMode}
                    onValueChange={(v) =>
                      setState((s) => ({
                        ...s,
                        defaultUploadMode: v as WizardState["defaultUploadMode"],
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="duplicate_winner">
                        Duplikuj winnera (Theriot orthodox — kopia best adsetu, nowa kreacja)
                      </SelectItem>
                      <SelectItem value="duplicate_specific">
                        Duplikuj konkretny adset (wybór ręczny w modalu)
                      </SelectItem>
                      <SelectItem value="add_to_existing">
                        Dodaj do istniejącego adsetu (bez duplikatu)
                      </SelectItem>
                      <SelectItem value="create_new">
                        Stwórz nowy adset z wybraną audience (audience picker)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Uwaga: tryb "Duplikuj winnera" wymaga ≥1 adsetu z konwersjami w 7 dniach. Bez
                    tego auto-upload zatrzyma się i poprosi o manualną decyzję.
                  </p>
                </Field>

                <Field>
                  <FieldLabel>Domyślny status nowych reklam</FieldLabel>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setState((s) => ({ ...s, defaultUploadStatus: "PAUSED" }))}
                      className={`flex-1 border rounded p-3 transition ${state.defaultUploadStatus === "PAUSED" ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}
                    >
                      <div className="font-medium text-sm">PAUSED (zalecane)</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Reklama utworzona w Meta, czeka na ręczną aktywację w Ads Manager.
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setState((s) => ({ ...s, defaultUploadStatus: "ACTIVE" }))}
                      className={`flex-1 border rounded p-3 transition ${state.defaultUploadStatus === "ACTIVE" ? "border-emerald-500 bg-emerald-500/10" : "border-white/10 hover:border-white/30"}`}
                    >
                      <div className="font-medium text-sm">ACTIVE</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Reklama startuje od razu live z budżetem winnera. Tylko jeśli ufasz
                        autopilotowi.
                      </div>
                    </button>
                  </div>
                </Field>
              </div>
            )}

            {step === 10 && (
              <CreativeStyleStep
                concepts={conceptLibraryQuery.data ?? []}
                loadingConcepts={conceptLibraryQuery.isLoading}
                selectedConceptIds={state.creativeConceptIds}
                setSelectedConceptIds={(ids) =>
                  setState((s) => ({ ...s, creativeConceptIds: ids }))
                }
                inspirationAssets={state.inspirationAssets}
                addInspirationAsset={(asset) =>
                  setState((s) => ({ ...s, inspirationAssets: [...s.inspirationAssets, asset] }))
                }
                removeInspirationAsset={(id) =>
                  setState((s) => ({
                    ...s,
                    inspirationAssets: s.inspirationAssets.filter((a) => a.id !== id),
                  }))
                }
                mixRatio={state.inspirationMixRatio}
                setMixRatio={(r) => setState((s) => ({ ...s, inspirationMixRatio: r }))}
                agencyClientId={selectedClientId ?? null}
                funnelType={state.funnelType}
                uploadFile={(file) => proxyUploadMut.mutateAsync({ file, folder: "firm-assets" })}
                createFirmAsset={(input) => createFirmAssetMut.mutateAsync(input)}
                showToast={(args) => toast(args)}
                isUploading={proxyUploadMut.isPending}
                isCreatingAsset={createFirmAssetMut.isPending}
              />
            )}

            {step === 9 && (
              <OfferSheetStep
                mode={state.offerSheetMode}
                setMode={(mode) => setState((s) => ({ ...s, offerSheetMode: mode }))}
                offerSheetId={state.offerSheetId}
                setOfferSheetId={(id) => setState((s) => ({ ...s, offerSheetId: id }))}
                draftTitle={state.offerSheetDraftTitle}
                setDraftTitle={(title) => setState((s) => ({ ...s, offerSheetDraftTitle: title }))}
                draftContent={state.offerSheetDraftContent}
                setDraftContent={(content) =>
                  setState((s) => ({ ...s, offerSheetDraftContent: content }))
                }
                sheets={offerSheetsQuery.data ?? []}
                loading={offerSheetsQuery.isLoading}
                defaultBrandName={selectedClientName ?? ""}
              />
            )}
          </CardContent>
        </Card>

        <div className="mt-6 flex justify-between">
          <Button variant="outline" onClick={prev} disabled={step === 1} className="gap-2">
            <ChevronLeft className="w-4 h-4" /> Wstecz
          </Button>
          {step < totalSteps ? (
            <Button onClick={next} disabled={!canProceed(step, state)} className="gap-2">
              Dalej <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={create.isPending} className="gap-2">
              {create.isPending ? "Tworzenie…" : "Utwórz sesję"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function canProceed(step: number, state: WizardState): boolean {
  switch (step) {
    case 1:
      return !!state.funnelType;
    case 2:
      return state.industry.length > 0 && !!state.market;
    case 3:
      return state.primaryKpiTarget > 0;
    case 4:
      return (
        state.dailyBudgetMax > 0 &&
        state.hardDeck >= 0 &&
        state.monthlyBudgetMax >= state.dailyBudgetMax
      );
    case 5:
      return /^act_\d+$/.test(state.metaAdAccountId) || state.metaAdAccountId === "";
    case 6:
      return state.slackChannelId === "" || /^C[A-Z0-9]+$/.test(state.slackChannelId);
    case 7:
      return true;
    case 8: {
      if (state.creativeMode === "manual_upload") return true;
      // AI/mix wymaga creativeTypes — readiness check robi się inline w UI (user widzi blocker
      // ale formalnie nie blokujemy "Dalej" — sesja powstanie jako draft i preflight zatrzyma
      // aktywację dopóki firm-assets nie będzie kompletny).
      return state.creativeTypes.length > 0;
    }
    case 9: {
      if (state.offerSheetMode === "skip") return true;
      if (state.offerSheetMode === "pick") return !!state.offerSheetId;
      // 'paste' — wymagamy minimalnej treści (50 znaków) i tytułu
      return (
        state.offerSheetDraftTitle.trim().length > 0 &&
        state.offerSheetDraftContent.trim().length >= 50
      );
    }
    case 10: {
      // Min 1 concept (max 3) ALBO przynajmniej 1 inspiration. Pusty step = fallback do legacy
      // (Claude wymyśla styl sam) — ale sygnalizujemy w UI że to słabe.
      const hasConcepts =
        state.creativeConceptIds.length >= 1 && state.creativeConceptIds.length <= 3;
      const hasInspiration = state.inspirationAssets.length > 0;
      return hasConcepts || hasInspiration;
    }
    default:
      return true;
  }
}

interface CreativeStyleStepProps {
  concepts: CreativeConceptSummary[];
  loadingConcepts: boolean;
  selectedConceptIds: string[];
  setSelectedConceptIds: (ids: string[]) => void;
  inspirationAssets: Array<{ id: string; thumbnailUrl: string; title: string }>;
  addInspirationAsset: (asset: { id: string; thumbnailUrl: string; title: string }) => void;
  removeInspirationAsset: (id: string) => void;
  mixRatio: number;
  setMixRatio: (r: number) => void;
  agencyClientId: string | null;
  funnelType: string;
  uploadFile: (file: File) => Promise<{ fileUrl: string; key: string }>;
  createFirmAsset: (input: {
    agencyClientId?: string | null;
    assetType: string;
    mediaType: "image" | "video" | "audio";
    title: string;
    fileUrl: string;
    thumbnailUrl?: string | null;
  }) => Promise<{ id: string; fileUrl: string; thumbnailUrl: string | null; title: string }>;
  showToast: (args: { title: string; description?: string; variant?: "destructive" }) => void;
  isUploading: boolean;
  isCreatingAsset: boolean;
}

function CreativeStyleStep(props: CreativeStyleStepProps): JSX.Element {
  const {
    concepts,
    loadingConcepts,
    selectedConceptIds,
    setSelectedConceptIds,
    inspirationAssets,
    addInspirationAsset,
    removeInspirationAsset,
    mixRatio,
    setMixRatio,
    agencyClientId,
    funnelType,
    uploadFile,
    createFirmAsset,
    showToast,
    isUploading,
    isCreatingAsset,
  } = props;

  const [uploadingFile, setUploadingFile] = useState(false);
  const uploadInputRef = useState<HTMLInputElement | null>(null);

  const toggleConcept = (id: string) => {
    if (selectedConceptIds.includes(id)) {
      setSelectedConceptIds(selectedConceptIds.filter((x) => x !== id));
    } else if (selectedConceptIds.length < 3) {
      setSelectedConceptIds([...selectedConceptIds, id]);
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadingFile(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) {
          showToast({
            title: "Pominięto plik",
            description: `${file.name} — tylko obrazy.`,
            variant: "destructive",
          });
          continue;
        }
        // Proxy upload (multipart przez backend → R2) eliminuje R2 CORS issue z localhost origin.
        // Krok 1: POST do backendu z plikiem; backend uploaduje do R2 i zwraca fileUrl.
        // Krok 2: rejestracja firm_creative_asset z assetType=style_reference dla klienta.
        let uploadResult: { fileUrl: string; key: string } | null = null;
        try {
          uploadResult = await uploadFile(file);
        } catch (err) {
          showToast({
            title: `Upload "${file.name}" — krok 1/2 nieudany (transfer do R2 przez backend)`,
            description: err instanceof Error ? err.message : String(err),
            variant: "destructive",
          });
          continue;
        }

        try {
          const asset = await createFirmAsset({
            agencyClientId,
            assetType: "style_reference",
            mediaType: "image",
            title: file.name.replace(/\.[^.]+$/, "").slice(0, 80) || "Inspiracja",
            fileUrl: uploadResult.fileUrl,
            thumbnailUrl: uploadResult.fileUrl,
          });
          addInspirationAsset({
            id: asset.id,
            thumbnailUrl: asset.thumbnailUrl ?? asset.fileUrl,
            title: asset.title,
          });
        } catch (err) {
          showToast({
            title: `Upload "${file.name}" — krok 2/2 nieudany (zapis do biblioteki)`,
            description: err instanceof Error ? err.message : String(err),
            variant: "destructive",
          });
        }
      }
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    <div className="space-y-5">
      <Field>
        <FieldLabel>Schematy kreacji (wybierz 1-3)</FieldLabel>
        <p className="text-xs text-muted-foreground mb-2">
          Autopilot generuje partię briefów — KAŻDY będzie używać innego schematu z tej listy
          (rotacja). Daje to wymuszoną różnorodność.
          {selectedConceptIds.length > 0 && (
            <>
              {" "}
              Aktualnie wybrane: <strong>{selectedConceptIds.length} / 3</strong>.
            </>
          )}
        </p>
        {loadingConcepts ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border border-white/10 rounded p-3">
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-3 w-full" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto pr-1">
            {concepts.map((c) => {
              const selected = selectedConceptIds.includes(c.id);
              const fitsFunnel =
                !funnelType || c.bestFor.length === 0 || c.bestFor.includes(funnelType);
              const disabled = !selected && selectedConceptIds.length >= 3;
              return (
                <button
                  key={c.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleConcept(c.id)}
                  className={`border rounded p-3 text-left transition ${selected ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-medium text-sm">{c.label}</span>
                    {fitsFunnel ? null : (
                      <Badge variant="outline" className="text-[9px]">
                        spoza funnelu
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{c.hint}</div>
                </button>
              );
            })}
          </div>
        )}
      </Field>

      <Field>
        <FieldLabel>
          Inspiracje wizualne (opcjonalnie — wgraj grafiki reklamowe które ci się podobają)
        </FieldLabel>
        <p className="text-xs text-muted-foreground mb-2">
          Każda inspiracja zostaje zapisana w bibliotece klienta jako{" "}
          <code className="text-[10px]">style_reference</code> i może być wykorzystana też przez
          ad-creator / vsl. Autopilot losowo używa ich jako reference image —{" "}
          <strong>{Math.round(mixRatio * 100)}%</strong> wygenerowanych wariantów będzie zachowywać
          layout/styl z inspiracji, reszta to pure generation.
        </p>

        <div className="flex items-center gap-3 mb-3">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            ref={(el) => {
              uploadInputRef[1](el);
            }}
            className="hidden"
            id="inspiration-file-input"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={uploadingFile || isUploading || isCreatingAsset}
            onClick={() => document.getElementById("inspiration-file-input")?.click()}
            className="gap-2"
          >
            {uploadingFile || isUploading || isCreatingAsset ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Wgraj grafiki (jpg/png)
          </Button>
          {inspirationAssets.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {inspirationAssets.length} inspirac
              {inspirationAssets.length === 1 ? "ja" : inspirationAssets.length < 5 ? "je" : "ji"}
            </span>
          )}
        </div>

        {inspirationAssets.length > 0 && (
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2 mb-3">
            {inspirationAssets.map((asset) => (
              <div key={asset.id} className="relative group border rounded overflow-hidden">
                <img
                  src={asset.thumbnailUrl}
                  alt={asset.title}
                  className="w-full h-24 object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeInspirationAsset(asset.id)}
                  className="absolute top-1 right-1 bg-black/70 text-white rounded-full w-5 h-5 text-xs leading-none hover:bg-red-600 opacity-0 group-hover:opacity-100 transition"
                  title="Usuń z wizardu (zostanie w bibliotece firm-assets)"
                >
                  ×
                </button>
                <div className="text-[9px] p-1 truncate" title={asset.title}>
                  {asset.title}
                </div>
              </div>
            ))}
          </div>
        )}

        {inspirationAssets.length > 0 && (
          <div className="border border-white/10 rounded p-2">
            <div className="text-xs mb-1">
              Mix ratio: <strong>{Math.round(mixRatio * 100)}%</strong> wariantów z inspiracji
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={10}
              value={Math.round(mixRatio * 100)}
              onChange={(e) => setMixRatio(Number(e.target.value) / 100)}
              className="w-full"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
              <span>0% (pure AI)</span>
              <span>100% (zawsze inspirowane)</span>
            </div>
          </div>
        )}

        {inspirationAssets.length === 0 && selectedConceptIds.length === 0 && (
          <div className="border border-amber-500/40 bg-amber-500/5 rounded p-3 text-xs text-amber-200">
            ⚠️ Bez schematów i bez inspiracji autopilot wymyśla styl sam (legacy fallback). Wybierz
            przynajmniej 1 schemat lub wgraj 1 inspirację żeby ruszyć dalej.
          </div>
        )}
      </Field>
    </div>
  );
}

interface OfferSheetStepProps {
  mode: WizardState["offerSheetMode"];
  setMode: (mode: WizardState["offerSheetMode"]) => void;
  offerSheetId: string | null;
  setOfferSheetId: (id: string | null) => void;
  draftTitle: string;
  setDraftTitle: (title: string) => void;
  draftContent: string;
  setDraftContent: (content: string) => void;
  sheets: OfferSheetSummary[];
  loading: boolean;
  defaultBrandName: string;
}

function OfferSheetStep({
  mode,
  setMode,
  offerSheetId,
  setOfferSheetId,
  draftTitle,
  setDraftTitle,
  draftContent,
  setDraftContent,
  sheets,
  loading,
  defaultBrandName,
}: OfferSheetStepProps): JSX.Element {
  const charCount = draftContent.trim().length;
  return (
    <div className="space-y-4">
      <Field>
        <FieldLabel>Czym karmimy AI przy generacji reklam?</FieldLabel>
        <p className="text-xs text-muted-foreground mb-2">
          Bez konkretnej oferty AI buduje hooki "z powietrza" (np. "most SaaS dies in silence"). Z
          arkuszem ofertowym (USP, ICP, cena, transformacja, gwarancja) Claude pisze briefy o{" "}
          <strong>Twoim</strong> produkcie. To NAJWAŻNIEJSZY krok dla jakości kreacji.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {[
            {
              id: "pick" as const,
              label: "📋 Z biblioteki",
              hint: "Masz już arkusz ofertowy w Gawronify — wybierz go.",
            },
            {
              id: "paste" as const,
              label: "✏️ Wklej tekst",
              hint: "Skopiuj swój pitch / landing page copy / sales script. Zapiszemy jako nowy arkusz.",
            },
            {
              id: "skip" as const,
              label: "⏭️ Pomiń (fallback)",
              hint: "AI buduje briefy tylko z branży + funnel. Generyczne wyniki — niezalecane.",
            },
          ].map((opt) => {
            const selected = mode === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setMode(opt.id)}
                className={`border rounded p-3 text-left transition ${selected ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}
              >
                <div className="font-medium text-sm">{opt.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{opt.hint}</div>
              </button>
            );
          })}
        </div>
      </Field>

      {mode === "pick" && (
        <Field>
          <FieldLabel>Wybierz arkusz ofertowy</FieldLabel>
          {loading ? (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="border border-white/10 rounded p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Skeleton className="h-3.5 w-3.5 shrink-0" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                  <Skeleton className="h-3 w-full" />
                </div>
              ))}
            </div>
          ) : sheets.length === 0 ? (
            <div className="border border-amber-500/40 bg-amber-500/5 rounded p-3 text-xs">
              <p className="text-amber-200 mb-1">Nie masz jeszcze żadnego arkusza w bibliotece.</p>
              <p className="text-muted-foreground">
                Wróć i wybierz "Wklej tekst" — wklejony arkusz zapisze się od razu w bibliotece i
                będzie dostępny dla ad-creatora / VSL / carousel-a w przyszłości.
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {sheets.map((sheet) => {
                const selected = offerSheetId === sheet.id;
                return (
                  <button
                    key={sheet.id}
                    type="button"
                    onClick={() => setOfferSheetId(sheet.id)}
                    className={`w-full border rounded p-3 text-left transition ${selected ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        <FileText className="h-3.5 w-3.5 text-cyan-400 shrink-0" />
                        <span className="font-medium text-sm">{sheet.title}</span>
                      </div>
                      {sheet.isActive && (
                        <Badge variant="secondary" className="text-[10px]">
                          aktywny
                        </Badge>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground line-clamp-2">
                      {sheet.content.slice(0, 220)}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </Field>
      )}

      {mode === "paste" && (
        <>
          <Field>
            <FieldLabel>Tytuł arkusza</FieldLabel>
            <Input
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder={
                defaultBrandName ? `Oferta ${defaultBrandName}` : "np. Oferta Q2 2026 — main offer"
              }
            />
          </Field>
          <Field>
            <FieldLabel>Treść oferty</FieldLabel>
            <p className="text-xs text-muted-foreground mb-2">
              Wklej co masz: pitch deck, landing page copy, sales script, opis produktu ze Stripe'a.
              Im więcej konkretu (USP, ICP/persona, cena, transformacja, gwarancja, social proof),
              tym lepiej Claude trafi w hook.
            </p>
            <Textarea
              value={draftContent}
              onChange={(e) => setDraftContent(e.target.value)}
              rows={12}
              placeholder={`Przykład:

PRODUKT: Viral Partners — platforma B2B partner channel automation.
ICP: SaaS founders 5-50 osób, ARR 500K-10M PLN, mają już ~20 partnerów ale tracą czas na onboarding.
USP: Partner-as-a-channel scale bez SDR-a; pay-per-result (zamiast pay-per-click).
CENA: od 2.5k PLN/mc, ROI ~6x w 90 dni.
TRANSFORMACJA: agencja przestaje gonić leady, partnerzy generują 30% MRR.
GWARANCJA: 30 dni money-back jeśli nie 10+ nowych partner deals.
SOCIAL PROOF: 87 SaaS founderów, case study Gawronify (3x ARR w 6mc).`}
            />
            <div className="flex items-center justify-between text-[11px] mt-1">
              <span className={charCount < 50 ? "text-amber-400" : "text-emerald-400"}>
                {charCount} / min 50 znaków
              </span>
              <span className="text-muted-foreground">
                Zapisze się w bibliotece pod podanym tytułem.
              </span>
            </div>
          </Field>
        </>
      )}

      {mode === "skip" && (
        <div className="border border-amber-500/40 bg-amber-500/5 rounded p-3 text-xs text-amber-200">
          ⚠️ Bez oferty AI dostaje tylko branżę + funnel jako kontekst. Briefy będą ogólnikowe (np.
          "Most SaaS dies in silence") zamiast konkretu o Twoim produkcie. Możesz dodać arkusz
          później w bibliotece i odpalić "Wygeneruj briefy" ręcznie.
        </div>
      )}
    </div>
  );
}

function ReadinessRow({ ok, label, missing }: { ok: boolean; label: string; missing: string }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      {ok ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
      ) : (
        <span className="inline-flex w-3.5 h-3.5 rounded-full border border-amber-400/60 shrink-0" />
      )}
      <span className={ok ? "text-emerald-200" : "text-amber-200"}>
        {label}
        {!ok && <span className="text-muted-foreground"> — brak: {missing}</span>}
      </span>
    </div>
  );
}

function stepTitle(step: number): string {
  return (
    [
      "1. Co sprzedajesz?",
      "2. Branża + rynek",
      "3. KPI cel + branżowy benchmark",
      "4. Budżety + minimum testów",
      "5. Konto Meta",
      "6. Slack notifications (opcjonalne)",
      "7. Tryb autopilota",
      "8. Co testujemy? (typy kreacji + approval gate)",
      "9. Oferta klienta — czym karmimy AI?",
      "10. Styl kreacji — schematy + inspiracje",
    ][step - 1] ?? ""
  );
}

interface OnboardingProgressShape {
  step: OnboardingStep;
  message: string;
  progress: number;
  error?: string;
  details?: {
    snapshotsInserted?: number;
    adsAnalyzed?: number;
    strategyGenerated?: boolean;
    briefsGenerated?: number;
  };
}

const ONBOARDING_STEPS: ReadonlyArray<{ id: OnboardingStep; label: string }> = [
  { id: "backfill_snapshots", label: "Pobieram dane z Meta Ads" },
  { id: "analyze_existing_ads", label: "Analizuję istniejące reklamy" },
  { id: "generate_strategy", label: "Generuję strategię 30-dniową" },
  { id: "generate_creatives", label: "Tworzę briefy + grafiki" },
  { id: "activating", label: "Aktywuję sesję" },
];

function OnboardingLoader({
  sessionId,
  progress,
  onSkip,
}: {
  sessionId: string;
  progress: OnboardingProgressShape | null;
  onSkip: () => void;
}) {
  const currentStepIdx = progress ? ONBOARDING_STEPS.findIndex((s) => s.id === progress.step) : -1;
  const isFailed = progress?.step === "failed";
  const isDone = progress?.step === "done";

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {isFailed
                ? "❌ Onboarding nie powiódł się"
                : isDone
                  ? "✅ Onboarding zakończony"
                  : "Onboarding sesji autopilota"}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Sesja ID: <code className="text-[10px]">{sessionId}</code>
            </p>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-1">
              <div className="h-2 bg-white/10 rounded overflow-hidden">
                <div
                  className={`h-full transition-all ${isFailed ? "bg-red-500" : isDone ? "bg-emerald-500" : "bg-primary"}`}
                  style={{ width: `${progress?.progress ?? 0}%` }}
                />
              </div>
              <div className="text-xs text-muted-foreground text-right">
                {progress?.progress ?? 0}%
              </div>
            </div>

            <ul className="space-y-3">
              {ONBOARDING_STEPS.map((s, idx) => {
                const isCurrent = !isFailed && !isDone && progress?.step === s.id;
                const isCompleted = isDone || (currentStepIdx > idx && !isFailed);
                const isPending = !isCompleted && !isCurrent;
                return (
                  <li key={s.id} className="flex items-center gap-3 text-sm">
                    {isCompleted && <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />}
                    {isCurrent && (
                      <Loader2 className="h-4 w-4 text-primary animate-spin shrink-0" />
                    )}
                    {isPending && (
                      <span className="h-4 w-4 rounded-full border border-white/20 shrink-0" />
                    )}
                    <span
                      className={
                        isCompleted
                          ? "text-emerald-200"
                          : isCurrent
                            ? "text-white"
                            : "text-white/40"
                      }
                    >
                      {s.label}
                    </span>
                  </li>
                );
              })}
            </ul>

            {progress?.details && (
              <div className="text-xs text-muted-foreground space-y-0.5 border-t border-white/10 pt-3">
                {progress.details.snapshotsInserted !== undefined && (
                  <div>Snapshotów pobranych: {progress.details.snapshotsInserted}</div>
                )}
                {progress.details.adsAnalyzed !== undefined && (
                  <div>Reklam przeanalizowanych: {progress.details.adsAnalyzed}</div>
                )}
                {progress.details.strategyGenerated !== undefined && (
                  <div>
                    Strategia:{" "}
                    {progress.details.strategyGenerated ? "wygenerowana" : "nie udało się"}
                  </div>
                )}
                {progress.details.briefsGenerated !== undefined &&
                  progress.details.briefsGenerated > 0 && (
                    <div>Briefów + grafik: {progress.details.briefsGenerated}</div>
                  )}
              </div>
            )}

            {isFailed && progress?.error && (
              <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded p-2">
                {progress.error}
              </div>
            )}

            {(isFailed || isDone) && (
              <div className="flex justify-end">
                <Button onClick={onSkip} variant={isFailed ? "destructive" : "default"}>
                  Przejdź do dashboardu
                </Button>
              </div>
            )}

            {!isFailed && !isDone && (
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={onSkip}>
                  Pomiń i przejdź do dashboardu
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
